// The numbers the play menu's Completion tab shows and shares: the time, words
// per minute and the per-mode stats, worked out from the level, the board and
// the server's record. No DOM.
import { planetsOf, scorePlay, type Mode, type PlayLevel, type PlayState, type ScheduleLayer, type Variant } from '@wits/core';
import type { PlayRecord } from '../api/types';

export interface PlayStats {
  /** The night, or the week's Monday for a week's play. */
  date: string;
  mode: Mode;
  variant: Variant;
  /** The night's own level, or the week's (named XL). */
  layer: ScheduleLayer;
  constellations: number;
  /** Letter stars in the player's constellations, and in the level. */
  starsUsed: number;
  starsTotal: number;
  /** Planets the player's constellations ringed, and in the level. */
  planetsRinged: number;
  planetsTotal: number;
  /** Start to finish; null while the play is open. */
  elapsedMs: number | null;
  wordsPerMinute: number | null;
  /** The constellation with the most stars (the first of equals). */
  longest: { word: string; stars: number } | null;
  /** Constellations returned to the stars along the way. */
  returned: number;
  /** Puzzle: valid words that were not part of the solution, in the order found. */
  extraWords: string[];
  /** Apogee: the final score once finished, the running score before. */
  score: number | null;
  starsPerConstellation: number | null;
  scorePerWord: number | null;
  best: { word: string; total: number } | null;
}

export function playStats(level: PlayLevel, board: PlayState, play: PlayRecord, extraWords: readonly string[] = []): PlayStats {
  const nodes = new Map(level.nodes.map((n) => [n.id, n]));
  const starsIn = (ids: readonly number[]) => ids.filter((id) => nodes.get(id)?.type === 'star').length;
  const constellations = board.constellations;
  const starsUsed = constellations.reduce((sum, c) => sum + starsIn(c.nodes), 0);
  const planetsRinged = constellations.filter((c) => c.planet).length;
  const starsTotal = level.nodes.filter((n) => n.type === 'star').length;

  let elapsedMs: number | null = null;
  if (play.finishedAt !== null) elapsedMs = Math.max(0, play.finishedAt - play.startedAt);
  const minutes = elapsedMs !== null ? elapsedMs / 60_000 : 0;
  const wordsPerMinute = elapsedMs !== null && elapsedMs >= 1000 ? constellations.length / minutes : null;

  let longest: PlayStats['longest'] = null;
  for (const c of constellations) {
    const stars = starsIn(c.nodes);
    if (!longest || stars > longest.stars) longest = { word: c.word, stars };
  }

  let score: number | null = null;
  let scorePerWord: number | null = null;
  let best: PlayStats['best'] = null;
  if (level.mode === 'apogee') {
    const scored = scorePlay(level, constellations);
    score = play.status === 'finished' ? (play.score?.final ?? scored.running) : scored.running;
    scorePerWord = constellations.length > 0 ? scored.running / constellations.length : null;
    for (const w of scored.words) if (!best || w.total > best.total) best = { word: w.word, total: w.total };
  }

  return {
    date: play.date,
    mode: level.mode,
    variant: level.variant,
    layer: play.layer ?? 'daily',
    constellations: constellations.length,
    starsUsed,
    starsTotal,
    planetsRinged,
    planetsTotal: planetsOf(level).length,
    elapsedMs,
    wordsPerMinute,
    longest,
    returned: play.moves.filter((m) => m.type === 'remove').length,
    extraWords: level.mode === 'puzzle' ? [...extraWords] : [],
    score,
    starsPerConstellation: constellations.length > 0 ? starsUsed / constellations.length : null,
    scorePerWord,
    best,
  };
}

// How a play is named and how a time reads are in the shared core
// (share/caption.ts), which the Worker's share page uses too.
export { formatDuration, playName, VARIANT_NAMES } from '@wits/core';

/** One decimal, without a trailing ".0". */
export function formatRate(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}
